/**
 * Printable Checklist Page
 * Displays shift start and end checklists in a print-friendly format
 */
import { useState, useEffect } from 'react';
import { Printer, ClipboardCheck, Clock, CheckCircle2, Circle, Sun, Moon } from 'lucide-react';
import api from '@/utils/apiClient';

export default function PrintChecklistPage() {
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchChecklists = async () => {
      try {
        // Fetch both checklists
        const [startRes, endRes] = await Promise.all([
          api.get('/sop/checklists/shift-start'),
          api.get('/sop/checklists/shift-end')
        ]);
        
        setChecklists([startRes.data, endRes.data]);
      } catch (error) {
        console.error('Error fetching checklists:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchChecklists();
  }, []);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <ClipboardCheck className="w-12 h-12 text-green-600 animate-pulse mx-auto mb-4" />
          <p className="text-gray-600">Loading checklists...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .page-break { page-break-after: always; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .checklist-section { break-inside: avoid; }
        }
        @page {
          size: A4;
          margin: 15mm;
        }
      `}</style>

      {/* Print Header - Only visible on screen */}
      <div className="no-print bg-green-600 text-white py-4 px-6 sticky top-0 z-50 shadow-lg">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ClipboardCheck className="w-8 h-8" />
            <div>
              <h1 className="text-xl font-bold">Bulaccino Cafe - Shift Checklists</h1>
              <p className="text-green-100 text-sm">Print-friendly version for supervisors</p>
            </div>
          </div>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-6 py-2 bg-white text-green-600 rounded-lg font-medium hover:bg-green-50 transition-colors"
            data-testid="print-btn"
          >
            <Printer className="w-5 h-5" />
            Print / Download PDF
          </button>
        </div>
      </div>

      {/* Printable Content */}
      <div className="max-w-4xl mx-auto p-6 bg-white">
        {checklists.map((checklist, idx) => (
          <div key={checklist.id} className={idx > 0 ? 'page-break pt-6' : ''}>
            {/* Checklist Header */}
            <div className="border-b-4 border-green-600 pb-4 mb-6">
              <div className="flex items-center gap-4">
                {checklist.id === 'shift-start' ? (
                  <Sun className="w-10 h-10 text-amber-500" />
                ) : (
                  <Moon className="w-10 h-10 text-indigo-500" />
                )}
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">{checklist.name}</h2>
                  <p className="text-gray-600">{checklist.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-6 mt-4 text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Estimated Time: ~{checklist.estimated_time_minutes} minutes
                </span>
                <span>Total Items: {checklist.sections.reduce((sum, s) => sum + s.items.length, 0)}</span>
                <span>Sections: {checklist.sections.length}</span>
              </div>
            </div>

            {/* Date/Shift Info Box */}
            <div className="border-2 border-gray-300 rounded-lg p-4 mb-6 bg-gray-50">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase font-medium mb-1">Date</p>
                  <div className="h-8 border-b-2 border-gray-400"></div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase font-medium mb-1">Shift</p>
                  <div className="h-8 border-b-2 border-gray-400"></div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase font-medium mb-1">Supervisor Name</p>
                  <div className="h-8 border-b-2 border-gray-400"></div>
                </div>
              </div>
            </div>

            {/* Sections */}
            <div className="space-y-6">
              {checklist.sections.map((section, sIdx) => (
                <div key={section.id} className="checklist-section border border-gray-300 rounded-lg overflow-hidden">
                  {/* Section Header */}
                  <div className="bg-gray-100 px-4 py-3 border-b border-gray-300">
                    <h3 className="text-lg font-bold text-gray-800">
                      {sIdx + 1}. {section.name}
                    </h3>
                  </div>

                  {/* Items */}
                  <div className="divide-y divide-gray-200">
                    {section.items.map((item, iIdx) => (
                      <div 
                        key={item.id}
                        className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50"
                      >
                        {/* Checkbox */}
                        <div className="flex-shrink-0 mt-0.5">
                          <div className="w-5 h-5 border-2 border-gray-400 rounded"></div>
                        </div>
                        
                        {/* Item Text */}
                        <div className="flex-1">
                          <p className={`text-sm ${item.required ? 'text-gray-900' : 'text-gray-600'}`}>
                            {item.text}
                          </p>
                        </div>
                        
                        {/* Required Badge */}
                        {item.required ? (
                          <span className="flex-shrink-0 text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded">
                            Required
                          </span>
                        ) : (
                          <span className="flex-shrink-0 text-xs text-gray-400 italic">
                            Optional
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Notes Section */}
            <div className="mt-6 border border-gray-300 rounded-lg p-4">
              <h4 className="text-sm font-bold text-gray-700 mb-2">Notes / Issues</h4>
              <div className="h-24 border-2 border-gray-200 rounded bg-gray-50"></div>
            </div>

            {/* Signature */}
            <div className="mt-6 grid grid-cols-2 gap-8">
              <div>
                <p className="text-xs text-gray-500 uppercase font-medium mb-2">Supervisor Signature</p>
                <div className="h-12 border-b-2 border-gray-400"></div>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-medium mb-2">Time Completed</p>
                <div className="h-12 border-b-2 border-gray-400"></div>
              </div>
            </div>

            {/* Legend - only on first page */}
            {idx === 0 && (
              <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg no-print">
                <h4 className="font-medium text-yellow-800 mb-2">Quick Guide</h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• <span className="text-red-600 font-medium">Required</span> items must be completed before opening/closing</li>
                  <li>• <span className="italic text-gray-500">Optional</span> items are recommended but not mandatory</li>
                  <li>• Use the Notes section to record any issues or equipment problems</li>
                  <li>• Submit the digital version in Admin Panel after completing the physical checklist</li>
                </ul>
              </div>
            )}
          </div>
        ))}

        {/* Footer */}
        <div className="mt-8 pt-4 border-t border-gray-300 text-center text-xs text-gray-500">
          <p>Bulaccino Cafe - Nadi, Fiji | Standard Operating Procedures</p>
          <p className="mt-1">Document generated from bulaccinocafe.com admin system</p>
        </div>
      </div>
    </>
  );
}
